import {
    IonContent,
    IonPage,
    IonRefresher,
    IonRefresherContent,
} from "@ionic/react";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Screens, useAnalytics } from "../AppAnalyticsProvider";
import { useAppState } from "../AppListenerProvider";
import { AppContext } from "../AppStateProvider";
import { useAuth } from "../AuthProvider";
import CustomHelmet from "../components/ComponentsFunctional/CustomHelmet/CustomHelmet";
import ExploreFollowVirtualizedList, { ExploreFollowVirtualizedListRef } from "../components/ComponentsUI/ExploreFollowVirtualizedList/ExploreFollowVirtualizedList";
import ExploreHotVirtualizedList, { ExploreHotVirtualizedListRef } from "../components/ComponentsUI/ExploreHotVirtualizedList/ExploreHotVirtualizedList";
import ExploreNewVirtualizedList, { ExploreNewVirtualizedListRef } from "../components/ComponentsUI/ExploreNewVirtualizedList/ExploreNewVirtualizedList";
import ExploreTVVirtualizedList from "../components/ComponentsUI/ExploreTVVirtualizedList/ExploreTVVirtualizedList";
import ToolbarSearchBarExplore from "../components/ComponentsUI/ToolbarSearchBarExplore/ToolbarSearchBarExplore";
import { usePushNotifications } from "../hooks/usePushNotifications";
import useSQLiteDB from "../hooks/useSQLiteDB";
import useTVTricks from "../hooks/useTVTricks";
import { UserType } from "../models/usertype";
import { ElementSizeService } from "../services/database/elementsize.service";
import UserService from "../services/user.service";

const ExplorePage: React.FC = () => {
    const { state } = useContext(AppContext);
    const { isAuthenticated, isLoading, user } = useAuth();
    const { platform } = useAppState();
    const { trackScreen, trackEvent } = useAnalytics();


    // GENERIC
    const [segmentValue, setSegmentValue] = React.useState(undefined);

    // TV 
    const tvListRef = useRef<ExploreHotVirtualizedListRef>(null);
    const { tricks } = useTVTricks(state.blockedUsers, state.blockingUsers, isAuthenticated);

    // FOLLOWING
    const followListRef = useRef<ExploreFollowVirtualizedListRef>(null);

    // NEW
    const newListRef = useRef<ExploreNewVirtualizedListRef>(null);

    // HOTT
    const hotListRef = useRef<ExploreHotVirtualizedListRef>(null)

    // GENERIC
    const [userType, setUserType] = React.useState(UserType.UNKNOWN);

    const { performSQLAction, initialized } = useSQLiteDB();
    const [elementSizeService, setElementSizeService] = useState<ElementSizeService | null>(null);

    const { permissionStatus, requestPushNotificationPermission } = usePushNotifications();

    useEffect(() => {
        const handleEnableNotifications = () => {
            // Only request if we're in prompt state
            if (permissionStatus === 'prompt' || permissionStatus === 'prompt-with-rationale') {
                requestPushNotificationPermission();
            }
        };

        handleEnableNotifications();
    }, [permissionStatus]);

    // Fire the canonical "home" screen on mount. Previously this fired
    // `explore_page` which meant the home tab was missing from the top-screens
    // dashboard (Screens.HOME is the name the rest of the analytics layer
    // expects). The inner segment tracking below still uses distinct names
    // (`explore_Following`, `explore_New`, etc.) so drill-down analytics keep
    // working for each tab.
    useEffect(() => {
        trackScreen(Screens.HOME, 'explore_component');
    }, [trackScreen]);


    useEffect(() => {
        const initService = async () => {
            if (!initialized) {
                setElementSizeService(null);
                return;
            }

            try {
                const service = new ElementSizeService(performSQLAction);
                setElementSizeService(service);
            } catch (error) {
                console.error("Failed to initialize ElementSizeService:", error);
                setElementSizeService(null);
            }

            window.prerenderReady = true;
        };

        initService();
    }, [initialized]);

    useEffect(() => {
        console.log("Loading HomePage - New - GETTING NEW TRICKS USE EFFECT");
        const getAuthInformation = async () => {
            if (isAuthenticated) {
                const user_type_retrieval = await UserService.getUserType(user.uid);
                setUserType(user_type_retrieval);
            }
        }

        if (!isLoading) {
            getAuthInformation();
        }
    }, [isAuthenticated, isLoading, user, state.blockedUsers.length, state.blockingUsers.length]);

    const handleSegmentChangeNew = async (segment: string) => {
        console.log("HANDLE SEGMENT CHANGE NEW: ", segment);
        setSegmentValue(segment);

        // Track screen view for each segment
        trackScreen(`explore_${segment.charAt(0).toUpperCase() + segment.slice(1)}`, 'explore_component');

        // Track segment change event
        trackEvent('explore_segment_change', {
            segment_name: segment,
            is_authenticated: isAuthenticated
        });

        try {
            // Set segment value first, then wait for next render cycle
            setTimeout(() => {
                if (segment === "explore") {
                    tvListRef.current?.scrollToTop();
                }
                else if (segment === "following") {
                    followListRef.current?.scrollToTop();
                }
                else if (segment === "new") {
                    newListRef.current?.scrollToTop();
                }
                else if (segment === "hot") {
                    hotListRef.current?.scrollToTop();
                }
            }, 0);
        } catch (e) {
            console.log("Error in handleSegmentChangeNew: ", e);
            // Track errors
            trackEvent('explore_segment_error', {
                segment_name: segment,
                error_message: e instanceof Error ? e.message : 'Unknown error',
            });
        }
    }

    return (
        <IonPage>
            <CustomHelmet title={"Ecliptic // A Ski and Snowboard Spot"}
                description={"Explore Ecliptic and Follow Skiers and Snowboarders To Create Your Own Feed. Search for any trick by name, rider, location, build your bag of tricks, and get noticed by the crew, sponsors or the world."}
                url={`https://ecliptic.day/home`} />
            <ToolbarSearchBarExplore handleSegmentChangeNew={handleSegmentChangeNew}
                isAuthenticated={isAuthenticated}></ToolbarSearchBarExplore>
            <IonContent scrollY={false}>
                <IonRefresher slot="fixed" onIonRefresh={async (event) => {
                    try {
                        trackEvent('explore_refresh', {
                            segment: segmentValue
                        });

                        if (segmentValue === 'explore') {
                            await tvListRef.current?.refreshTricks();
                        } else if (segmentValue === 'following') {
                            await followListRef.current?.refreshTricks();
                        } else if (segmentValue === 'new') {
                            await newListRef.current?.refreshTricks();
                        } else if (segmentValue === 'hot') {
                            await hotListRef.current?.refreshTricks();
                        }
                    } finally {
                        event.detail.complete();
                    }
                }}>
                    <IonRefresherContent />
                </IonRefresher>
                <div className="ion-content-scroll-host">
                    {
                        segmentValue === 'explore' ?
                            (
                                <ExploreTVVirtualizedList ref={tvListRef} elementSizeService={elementSizeService} externalTricks={tricks} />
                            )
                            : segmentValue === 'following' ?
                                (
                                    <ExploreFollowVirtualizedList ref={followListRef} elementSizeService={elementSizeService} />
                                ) :
                                segmentValue === 'new' ?
                                    (
                                        <ExploreNewVirtualizedList ref={newListRef} elementSizeService={elementSizeService} />
                                    ) :
                                    segmentValue === 'hot' ?
                                        (
                                            <ExploreHotVirtualizedList ref={hotListRef} elementSizeService={elementSizeService} />
                                        ) :
                                        null
                    }
                </div>
            </IonContent>
        </IonPage>
    );
};

export default ExplorePage;
